import React, { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import Button from './components/Button';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface StackFrame {
  fileName: string;
  lineNumber: number;
  columnNumber: number;
  functionName: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  private workspacePath = import.meta.env.VITE_WORKSPACE_PATH;
  private editor = import.meta.env.VITE_EDITOR;

  private parseStackTrace = (stack: string): StackFrame[] => {
    const lines = stack.split('\n');
    const frames: StackFrame[] = [];

    for (const line of lines) {
      // Parse different stack trace formats
      const chromeMatch = line.match(/^\s*at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
      const firefoxMatch = line.match(/^(.+?)@(.+?):(\d+):(\d+)/);

      if (chromeMatch) {
        frames.push({
          functionName: chromeMatch[1],
          fileName: chromeMatch[2],
          lineNumber: parseInt(chromeMatch[3]),
          columnNumber: parseInt(chromeMatch[4]),
        });
      } else if (firefoxMatch) {
        frames.push({
          functionName: firefoxMatch[1],
          fileName: firefoxMatch[2],
          lineNumber: parseInt(firefoxMatch[3]),
          columnNumber: parseInt(firefoxMatch[4]),
        });
      }
    }

    return frames;
  };

  private openInDevTools = (fileName: string, line: number, column: number) => {
    const isDevelopment = import.meta.env.DEV;

    if (!isDevelopment) {
      return;
    }

    try {
      const filePath = fileName.replace(window.location.origin, '').replace(/^\//, '');
      const absoluteFilePath = `${this.workspacePath}/${filePath}`;

      const cursorUrl = `${this.editor}://file${absoluteFilePath}:${line}:${column}`;

      const link = document.createElement('a');
      link.href = cursorUrl;
      link.click();
    } catch (error) {
      console.warn('Failed to open file in editor:', error);
    }
  };

  private ClickableStackTrace = ({ stack }: { stack: string }) => {
    const frames = this.parseStackTrace(stack);
    const isDevelopment = import.meta.env.DEV;

    return (
      <div className="space-y-1">
        {frames.map((frame, index) => {
          const isProjectFile = frame.fileName.includes('/src/');

          return (
            <div key={index} className="text-sh-xxxs">
              <span className="text-sh-grey-500">at </span>
              <span className="font-medium text-sh-grey-4e">{frame.functionName}</span>
              <span className="text-sh-grey-500"> (</span>
              {isDevelopment && isProjectFile && this.editor && this.workspacePath ? (
                <button
                  onClick={() => this.openInDevTools(frame.fileName, frame.lineNumber, frame.columnNumber)}
                  className="cursor-pointer text-blue-500 underline transition-colors hover:text-blue-700">
                  {frame.fileName.replace(window.location.origin, '')}:{frame.lineNumber}:{frame.columnNumber}
                </button>
              ) : (
                <span className="text-sh-grey-500">
                  {frame.fileName}:{frame.lineNumber}:{frame.columnNumber}
                </span>
              )}
              <span className="text-sh-grey-500">)</span>
            </div>
          );
        })}
      </div>
    );
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidMount(): void {
    const sentryKey = import.meta.env.VITE_SENTRY_DSN;

    if (!sentryKey) {
      console.error('Sentry DSN not provided. Error tracking disabled.');
      return;
    }

    Sentry.init({
      dsn: `${sentryKey}`,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0,
      //   tracesSampleRate: 0,
      //   beforeSend() {
      //     return null;
      //   },
    });
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      // throw new ForbiddenError on the server side
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((this.state.error as any).cause?.extensions?.code === 'FORBIDDEN') {
        window.location.href = '/not-authorized';
        return;
      }

      return (
        <div className="flex h-screen flex-col items-center justify-center bg-page-gradient">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md rounded-2xl border border-field-error/20 bg-white p-8 text-center shadow-light-red">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mb-6 flex justify-center">
              <ExclamationTriangleIcon className="size-16 text-field-error" />
            </motion.div>

            <h1 className="mb-4 font-cadiz text-sh-md font-bold text-black-100">Something went wrong</h1>
            <p className="mb-6 font-cadiz text-sh-xs text-tertiary-dark-gray">
              We encountered an unexpected error. Please try refreshing the page or contact support if the problem
              persists.
            </p>

            {this.state.error && (
              <motion.details
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mb-6 rounded-lg border border-sh-grey-eee bg-tertiary-light p-4 text-left">
                <summary className="cursor-pointer font-cadiz text-sh-xxs font-semibold text-tertiary-dark-gray transition-colors hover:text-black-100">
                  View technical details
                </summary>
                <div className="mt-3 font-cadiz text-sh-xxxs text-sh-grey-500">
                  <div className="mb-3 rounded border border-red-200 bg-red-50 p-2">
                    <strong className="text-field-error">Error:</strong> {this.state.error.toString()}
                  </div>

                  {this.state.error.stack && (
                    <div className="mb-3">
                      <strong className="mb-2 block text-sh-grey-4e">Stack Trace:</strong>
                      <div className="rounded border border-gray-200 bg-gray-50 p-2 font-mono">
                        <this.ClickableStackTrace stack={this.state.error.stack} />
                      </div>
                    </div>
                  )}

                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong className="mb-2 block text-sh-grey-4e">Component Stack:</strong>
                      <pre className="overflow-x-auto rounded border border-gray-200 bg-gray-50 p-2 text-sh-xxxs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </motion.details>
            )}

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
              <Button onClick={() => window.location.reload()} theme="solid-primary-600" size="lg" className="w-full">
                <ArrowPathIcon className="mr-2 size-5" />
                Refresh Page
              </Button>
            </motion.div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
